'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

// Floating product data cards
const floatingCards = [
{
  id: 'card-1',
  label: 'Bestseller',
  name: 'Luminous Veil Serum',
  detail: '30ml · Vitamin C + Retinol',
  price: '$89',
  rating: '4.9',
  reviews: '1,204',
  color: '#C9A96E',
  floatClass: 'float-1',
  position: 'top-[18%] left-[3%] lg:left-[5%]',
  delay: '0s'
},
{
  id: 'card-2',
  label: 'New Arrival',
  name: 'Rose Petal Radiance Oil',
  detail: '50ml · Pure Rosehip',
  price: '$72',
  rating: '4.8',
  reviews: '380',
  color: '#E8D5B0',
  floatClass: 'float-2',
  position: 'bottom-[22%] left-[2%] lg:left-[4%]',
  delay: '1.5s'
},
{
  id: 'card-3',
  label: 'Award Winner',
  name: 'Velvet Matte Lipstick',
  detail: '3.5g · 12 Shades',
  price: '$42',
  rating: '5.0',
  reviews: '2,891',
  color: '#1A1612',
  floatClass: 'float-3',
  position: 'top-[30%] right-[2%] lg:right-[4%]',
  delay: '0.8s'
}];


const heroStats = [
{ label: 'Products', value: '200+' },
{ label: 'Happy Customers', value: '48K+' },
{ label: 'Countries', value: '32' },
{ label: 'Avg Rating', value: '4.9★' }];


export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 20;
      const yPct = (clientY / innerHeight - 0.5) * 12;

      const mainImage = hero.querySelector<HTMLElement>('.hero-main-image');
      if (mainImage) {
        mainImage.style.transform = `translate(${xPct * 0.4}px, ${yPct * 0.4}px) scale(1.02)`;
      }
    };

    hero.addEventListener('mousemove', handleMouseMove);
    return () => hero.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-[#FAF8F3]"
      style={{ paddingTop: '80px' }}>
      
      {/* Noise Texture */}
      <div className="noise-overlay" />

      {/* Background Decorative Circles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(201,169,110,0.08)]"
          style={{ width: '90vw', height: '90vw' }} />
        

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(201,169,110,0.05)]"
          style={{ width: '65vw', height: '65vw' }} />
        

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(201,169,110,0.12)]"
          style={{ width: '40vw', height: '40vw' }} />
        

        {/* Gold glow */}
        <div
          className="absolute top-1/3 right-1/4"
          style={{
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(201,169,110,0.08) 0%, transparent 70%)',
            filter: 'blur(40px)'
          }} />
        
      </div>

      {/* Vertical Side Label */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-16 opacity-30">
        <div className="vertical-text font-sans text-[8px] font-bold tracking-[1.2em] uppercase text-[#8A7E72]">
          Spring_Collection_2026
        </div>
        <div
          className="w-px bg-gradient-to-b from-[#C9A96E] to-transparent"
          style={{ height: '80px' }} />
        

        <div className="vertical-text font-sans text-[8px] font-bold tracking-[1.2em] uppercase text-[#8A7E72]">
          Premium_Beauty
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 lg:px-16 w-full">
        <div className="grid grid-cols-12 gap-6 items-center min-h-[85vh]">
          {/* Left: Headline + CTA */}
          <div className="col-span-12 lg:col-span-4 order-2 lg:order-1 z-20">
            <div className="space-y-8">
              {/* Label */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-px bg-[#C9A96E]" />
                <span className="label-gold">Spring Collection 2026</span>
              </div>

              {/* Sub-headline */}
              <div>
                <p className="text-[15px] text-[#8A7E72] leading-relaxed max-w-xs">
                  Skin-first formulations, ethically sourced ingredients, and rituals that transform
                  your daily routine into a moment of pure luxury.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col gap-4">
                <Link href="/products">
                  <button className="btn-gold w-fit">Explore Collection</button>
                </Link>
                <Link href="/about">
                  <button className="btn-outline w-fit">Our Story</button>
                </Link>
              </div>

              {/* Trust Signal */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex -space-x-2">
                  {[
                  'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=80',
                  'https://images.pexels.com/photos/1036623/pexels-photo-1036623.jpeg?auto=compress&cs=tinysrgb&w=80',
                  'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=80'].
                  map((src, i) =>
                  <div
                    key={`avatar-${i}`}
                    className="w-8 h-8 rounded-full overflow-hidden border-2 border-[#FAF8F3]">
                    
                      <AppImage
                      src={src}
                      alt={`Happy customer ${i + 1} using LuxeBeauty products`}
                      className="w-full h-full object-cover" />
                    
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) =>
                    <span key={s} className="text-[#C9A96E] text-xs">
                        ★
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#8A7E72] font-medium mt-0.5">
                    48,000+ happy customers
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Center: Hero Image */}
          <div className="col-span-12 lg:col-span-8 order-1 lg:order-2 relative flex justify-center lg:justify-end">
            {/* Main Image Container */}
            <div
              className="relative"
              style={{ width: 'clamp(300px, 55vw, 680px)', height: 'clamp(400px, 75vh, 820px)' }}>
              
              {/* Background wash */}
              <div
                className="absolute inset-0 rounded-tl-[80px] rounded-br-[80px]"
                style={{
                  background: 'linear-gradient(135deg, #F0EBE0 0%, #E8D5B0 40%, #F5F0E8 100%)'
                }} />
              

              {/* Main Image */}
              <div
                className="hero-main-image absolute inset-4 overflow-hidden rounded-tl-[70px] rounded-br-[70px]"
                style={{ transition: 'transform 0.6s cubic-bezier(0.19, 1, 0.22, 1)' }}>
                
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_125990276-1767692216610.png"
                  alt="Elegant woman with glowing skin showcasing LuxeBeauty premium skincare products in soft natural light"
                  className="w-full h-full object-cover"
                  priority />
                

                {/* Gradient overlay bottom */}
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{
                    height: '40%',
                    background: 'linear-gradient(to top, rgba(240,235,224,0.6), transparent)'
                  }} />
                
              </div>

              {/* Hero Headline Overlay */}
              <div className="absolute top-8 left-0 right-0 flex justify-center z-10 pointer-events-none">
                <h1
                  className="font-fraunces text-center leading-[0.88] tracking-tight text-[#1A1612]"
                  style={{ fontSize: 'clamp(3.5rem, 9vw, 8rem)', fontWeight: 400 }}>
                  
                  Pure
                  <br />
                  <em className="gradient-gold not-italic">Luxe</em>
                  <br />
                  Beauty
                </h1>
              </div>

              {/* Floating Product Cards */}
              {floatingCards.map((card) =>
              <div
                key={card.id}
                className={`absolute ${card.position} z-20 ${card.floatClass}`}
                style={{ animationDelay: card.delay }}>
                
                  <div
                  className="glass-card rounded-xl p-3 shadow-lg"
                  style={{
                    width: 'clamp(140px, 18vw, 200px)',
                    borderLeft: `3px solid ${card.color}`
                  }}>
                  
                    <div className="flex items-start justify-between mb-1.5">
                      <span
                      className="text-[9px] font-bold tracking-widest uppercase"
                      style={{ color: card.color === '#1A1612' ? '#C9A96E' : card.color }}>
                      
                        {card.label}
                      </span>
                    </div>
                    <p className="text-[12px] font-semibold text-[#1A1612] leading-tight mb-0.5">
                      {card.name}
                    </p>
                    <p className="text-[10px] text-[#8A7E72] mb-2">{card.detail}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-bold text-[#1A1612]">{card.price}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[#C9A96E] text-[10px]">★</span>
                        <span className="text-[10px] text-[#8A7E72] font-medium">
                          {card.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Decorative Corner */}
              <div
                className="absolute -bottom-4 -left-4 w-16 h-16 border border-[rgba(201,169,110,0.3)]"
                style={{ borderRight: 'none', borderTop: 'none' }} />
              

              <div
                className="absolute -top-4 -right-4 w-16 h-16 border border-[rgba(201,169,110,0.3)]"
                style={{ borderLeft: 'none', borderBottom: 'none' }} />
              
            </div>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="border-t border-[rgba(201,169,110,0.12)] pt-8 pb-4 hidden md:flex justify-between items-center">
          {heroStats.map((stat) =>
          <div key={stat.label} className="flex flex-col gap-1">
              <span className="hud-label">{stat.label}</span>
              <span className="font-fraunces text-xl font-medium text-[#1A1612]">{stat.value}</span>
            </div>
          )}
          <div className="flex items-center gap-3 group cursor-pointer">
            <span className="text-[9px] font-bold tracking-widest uppercase text-[#C9A96E] opacity-60 group-hover:opacity-100 transition-opacity">
              Scroll to explore
            </span>
            <div className="w-7 h-7 rounded-full border border-[rgba(201,169,110,0.3)] flex items-center justify-center group-hover:bg-[rgba(201,169,110,0.1)] transition-all">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                
                <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>);

}